import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import BrimiLogo from "@/components/BrimiLogo";
import { ArrowRight, GraduationCap, Compass, Map, MessageSquare, Trophy, BrainCircuit } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1690223878423-14cf4867638f";
const QUIZ_IMG = "https://images.unsplash.com/photo-1541178735493-479c1a27ed24";
const COLL_IMG = "https://images.pexels.com/photos/7683694/pexels-photo-7683694.jpeg";

export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-[#0B1120] text-white grid-noise">
      {/* Nav */}
      <header className="glass-nav sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <BrimiLogo size={40} withBadge showText textColor="white" />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-semibold text-slate-300">
            <a href="#how" className="hover:text-white transition-colors">How it works</a>
            <a href="#features" className="hover:text-white transition-colors">Features</a>
            <a href="#fields" className="hover:text-white transition-colors">Career Fields</a>
          </nav>
          <div className="flex items-center gap-3">
            {user ? (
              <button data-testid="landing-goto-dashboard" onClick={() => nav("/dashboard")} className="brimi-pill accent-yellow text-sm">
                Go to Dashboard
              </button>
            ) : (
              <>
                <Link to="/login" data-testid="landing-login-link" className="text-sm font-bold text-slate-300 hover:text-white">
                  Log in
                </Link>
                <Link to="/register" data-testid="landing-get-started" className="brimi-pill accent-coral text-sm flex items-center gap-2">
                  Get Started <ArrowRight className="w-4 h-4" />
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero — asymmetric */}
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 space-y-6">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.2em] bg-white/5 border border-white/10 text-yellow-300">
            <BrainCircuit className="w-3.5 h-3.5" /> Grades IX – XII · India & Global
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Find your <span className="gradient-heading">right career</span><br />
            before you finish school.
          </h1>
          <p className="text-lg text-slate-300 max-w-xl">
            BrimiAI CareerCoach maps your interests, strengths and academic scores into a
            personalized roadmap — with the exact exams, streams and colleges to chase.
          </p>
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Link to="/register" data-testid="hero-cta-start" className="brimi-pill accent-coral text-base flex items-center gap-2">
              Start free · Take the Quiz <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/careers" data-testid="hero-cta-explore" className="brimi-pill bg-white/10 hover:bg-white/15 text-white text-base">
              Explore Careers
            </Link>
          </div>
          <div className="flex items-center gap-6 pt-4 text-sm text-slate-400">
            <span className="flex items-center gap-2"><Trophy className="w-4 h-4 text-yellow-400" /> 15+ career fields</span>
            <span className="flex items-center gap-2"><GraduationCap className="w-4 h-4 text-mint-400" /> India + Global colleges</span>
          </div>
        </div>

        <div className="lg:col-span-5 relative">
          <div className="absolute -top-6 -left-4 w-24 h-24 rounded-3xl accent-yellow rotate-6 z-0" />
          <div className="absolute -bottom-6 -right-4 w-28 h-28 rounded-3xl accent-mint -rotate-6 z-0" />
          <img
            src={HERO_IMG}
            alt="Students studying together"
            className="relative z-10 w-full h-[440px] object-cover rounded-3xl border-4 border-white/10 shadow-2xl"
          />
          <div className="absolute -bottom-8 left-6 z-20 brimi-card p-4 max-w-[240px]">
            <div className="flex items-center gap-2 text-sm font-bold text-slate-900">
              <div className="w-2.5 h-2.5 rounded-full bg-mint-500 animate-pulse" style={{ background: "#34D399" }} />
              AI matched 92%
            </div>
            <p className="text-xs text-slate-500 mt-1">Priya · Computer Science + Design</p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="max-w-7xl mx-auto px-6 py-16">
        <p className="text-[11px] uppercase tracking-[0.2em] text-yellow-300 mb-3">How it works</p>
        <h2 className="font-display text-3xl md:text-4xl mb-10 max-w-2xl">
          Four simple steps from confusion to clarity.
        </h2>
        <div className="grid md:grid-cols-4 gap-4">
          {[
            { n: "01", t: "Tell us about you", d: "Grade, subjects, marks, interests, strengths." },
            { n: "02", t: "Take the RIASEC quiz", d: "30 questions map your personality to careers." },
            { n: "03", t: "Get your roadmap", d: "AI generates a step-by-step plan for your target career." },
            { n: "04", t: "Explore colleges", d: "India + Global options, filtered by field and exam." },
          ].map((s) => (
            <div key={s.n} className="brimi-card p-6">
              <p className="font-display text-3xl text-coral-500" style={{ color: "#FB7185" }}>{s.n}</p>
              <p className="font-bold mt-3 text-lg text-slate-900">{s.t}</p>
              <p className="text-sm text-slate-500 mt-1">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Feature grid */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-12 gap-4">
          <div className="md:col-span-7 brimi-card p-8 flex flex-col justify-between min-h-[280px]" style={{ background: "linear-gradient(135deg, #FBBF24, #FB7185)" }}>
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-slate-900/70">AI Coach</p>
              <h3 className="font-display text-3xl md:text-4xl mt-2 text-slate-900">Talk to your career mentor, 24/7.</h3>
              <p className="text-slate-900/80 mt-3 max-w-lg">Ask about streams after Class X, JEE vs NEET, best design colleges abroad — anything. Get honest, personalized guidance.</p>
            </div>
            <MessageSquare className="w-16 h-16 text-slate-900/70 self-end" />
          </div>
          <div className="md:col-span-5 brimi-card p-8 min-h-[280px] bg-white flex flex-col justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Roadmap</p>
              <h3 className="font-display text-2xl md:text-3xl mt-2 text-slate-900">A step-by-step plan for every grade.</h3>
              <p className="text-slate-600 mt-3">Milestones from your current class till your dream job — with exams, skills and timelines.</p>
            </div>
            <Map className="w-12 h-12 text-electric-500" style={{ color: "#3B82F6" }} />
          </div>
          <div className="md:col-span-5 brimi-card p-8 min-h-[280px] flex flex-col justify-between" style={{ background: "#0F1B33", color: "white", border: "1px solid rgba(255,255,255,0.08)" }}>
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-mint-400" style={{ color: "#34D399" }}>Interest Quiz</p>
              <h3 className="font-display text-2xl md:text-3xl mt-2 text-white">Discover your RIASEC profile.</h3>
              <p className="text-slate-300 mt-3">Realistic · Investigative · Artistic · Social · Enterprising · Conventional. Backed by decades of career science.</p>
            </div>
            <ClipboardHint />
          </div>
          <div className="md:col-span-7 brimi-card p-0 min-h-[280px] overflow-hidden relative">
            <img src={COLL_IMG} alt="Campus" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/40" />
            <div className="relative z-10 p-8 flex flex-col justify-end h-full text-white">
              <p className="text-[11px] uppercase tracking-[0.2em] text-yellow-300">Colleges</p>
              <h3 className="font-display text-2xl md:text-3xl mt-2">India + Global. All in one place.</h3>
              <p className="text-slate-200 mt-2 max-w-md">Filter by field, region, and entrance exam. Save your favorites.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Fields strip */}
      <section id="fields" className="max-w-7xl mx-auto px-6 py-16">
        <p className="text-[11px] uppercase tracking-[0.2em] text-yellow-300 mb-3">Career Fields we cover</p>
        <h2 className="font-display text-3xl md:text-4xl mb-8 max-w-2xl">From medicine to metaverse, we&apos;ve got you.</h2>
        <div className="flex flex-wrap gap-2">
          {["Engineering", "Medical", "Law", "Commerce", "Sports", "Architecture", "Teaching", "Design & Arts", "Data Science", "Business", "Psychology", "Media", "Entrepreneurship"].map((f) => (
            <span key={f} className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm font-semibold text-slate-200 hover:bg-white/10 transition-colors">
              {f}
            </span>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 py-20">
        <div className="brimi-card p-10 md:p-14 text-center relative overflow-hidden" style={{ background: "linear-gradient(135deg, #3B82F6, #0F1B33)", color: "white" }}>
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full accent-yellow blur-2xl opacity-40" />
          <h2 className="font-display text-3xl md:text-5xl">Ready to plan your future?</h2>
          <p className="text-slate-200 mt-3 max-w-xl mx-auto">Sign up free. No credit card. Discover your path in under 10 minutes.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/register" data-testid="footer-cta-register" className="brimi-pill accent-yellow">Create free account</Link>
            <Link to="/login" data-testid="footer-cta-login" className="brimi-pill bg-white/10 text-white">I already have one</Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5 py-8 text-center text-slate-500 text-sm">
        © 2026 BrimiAI CareerCoach — Made for students of grades IX–XII.
      </footer>
    </div>
  );
}

function ClipboardHint() {
  return (
    <div className="flex gap-2 mt-6">
      {["R","I","A","S","E","C"].map((c, i) => (
        <div key={c} className="w-9 h-9 rounded-lg bg-white/10 border border-white/10 flex items-center justify-center font-display text-sm text-white" style={{ opacity: 0.4 + i * 0.1 }}>
          {c}
        </div>
      ))}
    </div>
  );
}
